<template>
  <Teleport to="body">
    <div v-if="modelValue" ref="backdropRef" class="modal-backdrop-v2" :class="{ 'modal-backdrop-draggable-v2': draggable }" :style="{ zIndex: zIndexEfetivo }">
      <div ref="modalRef" class="modal-card shadow" role="dialog" aria-modal="true" :class="tamanhoClass" :style="modalStyle">
        <header
          class="modal-header-v2 d-flex align-items-center justify-content-between"
          :class="{ 'cursor-grab': draggable, 'cursor-grabbing': draggable && dragging }"
          :style="{ backgroundColor: corHeader }"
          @mousedown.self="iniciarArrasto"
        >
          <slot name="header">
            <h5 class="modal-title-v2 mb-0">{{ titulo }}</h5>
          </slot>
          <button type="button" class="btn-close btn-close-white" @click="tentarFechar" aria-label="Fechar"></button>
        </header>

        <div class="modal-body-v2">
          <slot></slot>
        </div>

        <footer class="modal-footer-v2">
          <slot name="footer">
            <div class="d-flex gap-2 w-100 justify-content-end">
              <button type="button" class="btn btn-outline-secondary" @click="tentarFechar" :disabled="loading">
                Cancelar
              </button>
              <button type="button" class="btn btn-info text-white fw-bold" @click="confirmar" :disabled="loading">
                <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
                Salvar
              </button>
            </div>
          </slot>
        </footer>
      </div>
    </div>
  </Teleport>

  <!-- Modal de Confirmação para sair com alterações não salvas -->
  <Teleport to="body">
    <ConfirmationModal
      ref="confirmacaoSairRef"
      title="Alterações não salvas"
      :message="'Existem alterações não salvas neste formulário.<br><strong>Deseja realmente sair?</strong>'"
      confirm-button-text="Sair sem salvar"
      cancel-button-text="Continuar editando"
      @confirmed="onConfirmacaoSair"
    />
  </Teleport>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, nextTick, watch } from 'vue'
import ConfirmationModal from './ConfirmationModal.vue'
import { reservarZIndex, liberarZIndex, BASE_ZINDEX } from '@/composables/useZIndex'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  titulo: { type: String, default: '' },
  loading: { type: Boolean, default: false },
  tamanho: { type: String, default: 'lg' }, // xs, sm, lg, xl, xxl
  corHeader: { type: String, default: 'var(--bs-info)' },
  confirmarAoSair: { type: Boolean, default: false },
  // Quando informado (true/false), o confirmar-ao-sair só pergunta ao fechar se
  // houver alterações (temAlteracoes === true). null (não informado) mantém o
  // comportamento original: pergunta sempre que confirmarAoSair é true.
  temAlteracoes: { type: Boolean, default: null },
  zIndex: { type: Number, default: null },
  draggable: { type: Boolean, default: true }
})

const emit = defineEmits(['update:modelValue', 'confirmar'])

const backdropRef = ref(null)
const modalRef = ref(null)
const confirmacaoSairRef = ref(null)
const fechamentoPendente = ref(false)
const usuarioArrastou = ref(false)
let roModal = null

// --- Empilhamento (z-index) via registry central ---
// Reserva o próximo z-index ao abrir e libera ao fechar, garantindo que este
// modal fique sempre à frente de quem já estava aberto. A prop `zIndex`, quando
// informada, atua como piso (override opcional).
const tokenZIndex = ref(null)
const zIndexReservado = ref(null)
const zIndexEfetivo = computed(() => zIndexReservado.value ?? props.zIndex ?? BASE_ZINDEX)

// --- Estado de arrasto ---
const posX = ref(0)
const posY = ref(0)
const dragging = ref(false)
const dragOffset = ref({ x: 0, y: 0 })

const modalStyle = computed(() => {
  if (!props.draggable) return {}
  return {
    position: 'fixed',
    left: `${posX.value}px`,
    top: `${posY.value}px`,
    transform: 'none',
    margin: '0'
  }
})

const centralizarModal = () => {
  if (!props.draggable || !modalRef.value) return
  const rect = modalRef.value.getBoundingClientRect()
  posX.value = (window.innerWidth - rect.width) / 2
  posY.value = Math.max((window.innerHeight - rect.height) / 2, 20)
}

const iniciarArrasto = (e) => {
  if (!props.draggable || !modalRef.value) return
  e.preventDefault()
  usuarioArrastou.value = true
  dragging.value = true
  const rect = modalRef.value.getBoundingClientRect()
  dragOffset.value = { x: e.clientX - rect.left, y: e.clientY - rect.top }
  document.body.style.userSelect = 'none'
  document.body.style.pointerEvents = 'none'
  document.addEventListener('mousemove', mover)
  document.addEventListener('mouseup', pararArrasto)
}

const mover = (e) => {
  if (!dragging.value) return
  posX.value = e.clientX - dragOffset.value.x
  posY.value = e.clientY - dragOffset.value.y
}

const pararArrasto = () => {
  dragging.value = false
  document.body.style.userSelect = ''
  document.body.style.pointerEvents = ''
  document.removeEventListener('mousemove', mover)
  document.removeEventListener('mouseup', pararArrasto)
}

const limparListenersArrasto = () => {
  document.removeEventListener('mousemove', mover)
  document.removeEventListener('mouseup', pararArrasto)
  document.body.style.userSelect = ''
  document.body.style.pointerEvents = ''
}

const tentarFechar = () => {
  // Só confirma a saída quando há alterações de fato. `temAlteracoes` nula
  // (não informada) preserva o comportamento original (sempre perguntar).
  const deveConfirmar = props.confirmarAoSair &&
    (props.temAlteracoes === null || props.temAlteracoes === true)
  if (deveConfirmar) {
    fechamentoPendente.value = true
    confirmacaoSairRef.value?.show()
  } else {
    fechar()
  }
}

const onConfirmacaoSair = (confirmou) => {
  if (confirmou) {
    fechar()
  }
  fechamentoPendente.value = false
}

const fechar = () => emit('update:modelValue', false)
const confirmar = () => emit('confirmar')

const tamanhoClass = computed(() => {
  if (props.tamanho === 'xs') return 'modal-xs'
  if (props.tamanho === 'sm') return 'modal-sm'
  if (props.tamanho === 'xl') return 'modal-xl'
  if (props.tamanho === 'xxl') return 'modal-xxl'
  if (props.tamanho === 'full') return 'modal-full'
  return ''
})

const handleKeydown = (e) => {
  if (!props.modelValue) return
  if (!backdropRef.value?.contains(e.target)) return

  if (e.key === 'Escape') {
    e.preventDefault()
    tentarFechar()
  }
  if (e.key === 'Enter' && !props.loading) {
    if (e.target.tagName !== 'TEXTAREA' && !e.shiftKey) {
      e.preventDefault()
      e.stopPropagation()
      confirmar()
    }
  }
}

watch(() => props.modelValue, (novoValor) => {
  if (novoValor) {
    const { token, zIndex } = reservarZIndex(props.zIndex)
    tokenZIndex.value = token
    zIndexReservado.value = zIndex
  } else {
    liberarZIndex(tokenZIndex.value)
    tokenZIndex.value = null
    zIndexReservado.value = null
  }

  if (novoValor && props.draggable) {
    usuarioArrastou.value = false
    nextTick(() => {
      centralizarModal()
      // Re-centraliza enquanto o conteúdo carrega (o modal muda de altura)
      if (modalRef.value) {
        roModal?.disconnect()
        roModal = new ResizeObserver(() => {
          if (!usuarioArrastou.value) centralizarModal()
        })
        roModal.observe(modalRef.value)
      }
    })
  } else {
    roModal?.disconnect()
    roModal = null
  }
}, { immediate: true })

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  limparListenersArrasto()
  roModal?.disconnect()
  // Segurança: libera o z-index caso o modal seja desmontado ainda aberto
  liberarZIndex(tokenZIndex.value)
  tokenZIndex.value = null
  zIndexReservado.value = null
})
</script>

<style scoped>
.modal-backdrop-v2 {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-backdrop-draggable-v2 {
  display: block;
}

.modal-card {
  width: 90%;
  max-width: 800px;
  background-color: var(--bs-body-bg);
  color: var(--bs-body-color);
  border: 1px solid var(--bs-border-color-translucent);
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.modal-xs { max-width: 340px; }
.modal-sm { max-width: 500px; }
.modal-xl { max-width: 1140px; }
.modal-xxl { max-width: 1400px; }
.modal-full { width: 98vw; max-width: 1800px; max-height: 95vh; }

.modal-header-v2 {
  padding: 1rem 1.5rem;
  color: #FFFFFF;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.cursor-grab { cursor: grab; }
.cursor-grabbing { cursor: grabbing; user-select: none; }

.modal-title-v2 {
  font-size: 1.25rem;
  font-weight: 500;
  margin: 0;
  cursor: text;
  user-select: text;
}

.modal-body-v2 {
  padding: 1.5rem;
  overflow-y: auto;
  flex-grow: 1;
}

.modal-footer-v2 {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--bs-border-color-translucent);
  flex-shrink: 0;
  display: flex;
  background-color: var(--bs-tertiary-bg);
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}
</style>
